import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from 'components/Kizen/Icon';
import { StyledIconButtonMenu } from './styles';

export default function DashletMenu({
  onDeleteHandler,
  onEditHandler,
  onEditWithAIHandler,
  onRenameHandler,
  onDuplicateHandler,
  customMenuItems = [],
  canEdit = true,
  id,
  entity,
}) {
  const { t } = useTranslation();

  const handleChooseValue = useCallback(
    ({ value }) => {
      const customItem = customMenuItems.find((item) => item.value === value);
      if (customItem) {
        return customItem.onClick();
      }

      switch (value) {
        case 'rename':
          return onRenameHandler();
        case 'edit':
          return onEditHandler();
        case 'editWithAI': // special case for kizen ai plugin
          return onEditWithAIHandler();
        case 'duplicate':
          return onDuplicateHandler();
        case 'delete':
          return onDeleteHandler();
        default:
          return;
      }
    },
    [
      onRenameHandler,
      onEditHandler,
      onEditWithAIHandler,
      onDeleteHandler,
      onDuplicateHandler,
      customMenuItems,
    ]
  );

  const options = useMemo(() => {
    const customItems = customMenuItems.map((item) => ({
      value: item.value,
      label: item.label,
    }));

    const standardItems = canEdit
      ? [
          onRenameHandler
            ? {
                value: 'rename',
                label: t('Rename'),
              }
            : undefined,
          onEditHandler
            ? {
                value: 'edit',
                label: t('Edit'),
              }
            : undefined,
          onEditWithAIHandler
            ? {
                value: 'editWithAI',
                label: t('Edit With AI'),
              }
            : undefined,
          onDuplicateHandler
            ? {
                value: 'duplicate',
                label: t('Duplicate'),
              }
            : undefined,
          onDeleteHandler
            ? {
                value: 'delete',
                label: t('Delete'),
              }
            : undefined,
        ].filter(Boolean)
      : [];

    return [...customItems, ...standardItems];
  }, [
    customMenuItems,
    canEdit,
    onRenameHandler,
    onEditHandler,
    onEditWithAIHandler,
    onDuplicateHandler,
    onDeleteHandler,
    t,
  ]);

  return (
    <StyledIconButtonMenu
      sizing="dense"
      position="right"
      title={t('Edit {{entity}}', { entity: entity ?? t('Dashlet') })}
      onChange={handleChooseValue}
      options={options}
      className="no-drag dashlet-menu"
      overlay={true}
    >
      <Icon icon="three-dot" data-qa-dashlet-id={id} />
    </StyledIconButtonMenu>
  );
}
